import { useMemo, useState } from "react";
import { Avatar } from "./Avatar";
import { TextInput } from "./Field";
import type { DirectoryPerson } from "../data/types";
import { initialsOf } from "../../../src/shared/names";

/** Where the Hub's people stand: still loading, loaded, or unreadable (with why). */
export type DirectoryState = { kind: "loading" } | { kind: "ready"; people: DirectoryPerson[] } | { kind: "error"; reason: string };

const BUTTON_QUIET = "rounded-md px-2 py-1 text-xs font-medium text-[var(--text-2)] hover:text-[var(--text)] focus-visible:focus-ring";

/**
 * Settings › Team › Add member: pick a person from the Hub instead of typing
 * a name. Only people not already on the team are offered. The server takes
 * the name, email and photo from the Hub; the admin chooses only the role.
 *
 * The Hub being unreadable is not a dead end: the reason is shown and the
 * manual add (the old free-text form) is one click away, as it is when the
 * person is not in the Hub at all.
 */
export function HubPeoplePicker({
  directory,
  onAdd,
  onManual,
  onCancel,
}: {
  directory: DirectoryState;
  onAdd: (hubUserId: string, role: string) => Promise<void>;
  onManual: () => void;
  onCancel: () => void;
}) {
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<DirectoryPerson | null>(null);
  const [role, setRole] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const available = useMemo(() => {
    if (directory.kind !== "ready") return [];
    const q = query.trim().toLowerCase();
    return directory.people.filter((p) => p.memberId === null && (q === "" || p.name.toLowerCase().includes(q) || p.email.includes(q)));
  }, [directory, query]);

  const choose = (p: DirectoryPerson) => {
    setSelected(p);
    setRole(p.jobTitle ?? "Engineer");
    setError(null);
  };

  const add = async () => {
    if (!selected) return;
    setBusy(true);
    setError(null);
    try {
      await onAdd(selected.hubUserId, role.trim());
    } catch (e) {
      // The API client throws a Response whose body is the server's reason.
      const reason = e instanceof Response ? await e.text() : e instanceof Error ? e.message : "";
      setError(reason || "Could not add member");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border-b border-[var(--border)] px-4 py-3">
      {directory.kind === "loading" && <p className="text-sm text-[var(--text-2)]">Loading people from the Hub…</p>}

      {directory.kind === "error" && (
        <p className="text-sm text-[var(--text-2)]">
          Could not load people from the Hub ({directory.reason}). You can still add someone manually.
        </p>
      )}

      {directory.kind === "ready" && (
        <>
          <TextInput aria-label="Search people in the Hub" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search people in the Hub" autoFocus />
          <ul aria-label="People in the Hub" className="mt-2 max-h-64 overflow-y-auto rounded-md border border-[var(--border)]">
            {available.length === 0 && (
              <li className="px-3 py-2 text-sm text-[var(--text-2)]">
                {query.trim() === "" ? "Everyone in the Hub is already on the team." : "Nobody in the Hub matches that."}
              </li>
            )}
            {available.map((p) => (
              <li key={p.hubUserId}>
                <button
                  type="button"
                  aria-pressed={selected?.hubUserId === p.hubUserId}
                  onClick={() => choose(p)}
                  className={[
                    "flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-gray-100 focus-visible:focus-ring",
                    selected?.hubUserId === p.hubUserId ? "bg-brand-50" : "",
                  ].join(" ")}
                >
                  <Avatar owner={{ name: p.name, initials: initialsOf(p.name), color: "gray", avatarUrl: p.avatarUrl }} size="sm" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate">{p.name}</span>
                    <small className="block truncate text-xs text-[var(--text-2)]">
                      {p.email}
                      {p.jobTitle ? ` · ${p.jobTitle}` : ""}
                    </small>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </>
      )}

      <div className="mt-3 flex items-center gap-2">
        {selected && (
          <TextInput aria-label="Member role" value={role} onChange={(e) => setRole(e.target.value)} placeholder="Role" className="max-w-[200px]" />
        )}
        <button type="button" onClick={onManual} className={`${BUTTON_QUIET} mr-auto`}>
          Add manually
        </button>
        <button type="button" onClick={onCancel} className={BUTTON_QUIET}>
          Cancel
        </button>
        <button
          type="button"
          disabled={busy || !selected || role.trim() === ""}
          onClick={add}
          className="rounded-md bg-brand-600 px-2 py-1 text-xs font-semibold text-white hover:bg-brand-700 focus-visible:focus-ring disabled:opacity-50"
        >
          {selected ? `Add ${selected.name.split(/\s+/)[0] ?? ""}` : "Add"}
        </button>
      </div>
      {error && <p className="mt-2 text-xs text-error-600">{error}</p>}
    </div>
  );
}
